import {
  type LucideIcon,
  MessagesSquare,
  PhoneForwarded,
  PhoneIncoming,
  PhoneOutgoing,
} from "lucide-react"
import { useEffect, useState } from "react"

import { cn } from "@/lib/utils"

interface FeedEvent {
  icon: LucideIcon
  title: string
  detail: string
  /** Omitted = no outcome chip, as the real dashboard renders info-only. */
  chip?: string
  live?: boolean
}

/**
 * Today's activity, newest first. Titles are "{job} {did a thing}" — the
 * employees at work, never over-explained — and the detail line carries the
 * real data (numbers, names, times, durations). Chip labels come from the
 * platform's outcome vocabulary; a row can go chipless the way the real
 * dashboard renders info-only calls.
 */
const EVENTS: FeedEvent[] = [
  {
    icon: PhoneIncoming,
    title: "Receptionist is answering a call",
    detail: "Now · (602) 555-0134 · 0:47",
    chip: "Live",
    live: true,
  },
  {
    icon: PhoneForwarded,
    title: "Receptionist transferred a new lead",
    detail: "6:48 PM · (480) 555-0182 · 2m 04s",
    chip: "Transferred",
  },
  {
    icon: MessagesSquare,
    title: "Website chat handled an inquiry",
    detail: "3:12 PM · Sofia Reyes · 9 messages",
    chip: "Resolved",
  },
  {
    icon: PhoneIncoming,
    title: "Receptionist took a message",
    detail: "11:05 AM · (415) 555-0119 · 1m 03s",
    chip: "Message taken",
  },
  {
    icon: PhoneOutgoing,
    title: "Lead responder booked an appointment",
    detail: "9:12 AM · Dan Okafor · responded in 47s",
    chip: "Appointment booked",
  },
]

/* Rows reveal oldest-first (bottom-up), each new one pushing the feed down,
   with a longer beat before the live call lands on top. One entry per event. */
const REVEAL_DELAYS = [1200, 2600, 4000, 5400, 7600]

/**
 * The product window that bleeds off the hero panel's bottom edge: a live
 * activity feed. The top row arrives a beat after load so the desk reads as
 * working right now, not as a screenshot.
 */
export function ProductPanel() {
  // Reduced motion resolves in the initial state rather than an effect, so the
  // feed paints complete on the first frame instead of flashing empty.
  const [revealed, setRevealed] = useState(() =>
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? EVENTS.length
      : 0,
  )

  useEffect(() => {
    if (revealed === EVENTS.length) return
    const timers = REVEAL_DELAYS.map((delay, i) =>
      setTimeout(() => setRevealed(i + 1), delay),
    )
    return () => timers.forEach(clearTimeout)
    // Runs once on mount: the schedule is fixed and re-running it on each
    // reveal would restart the whole sequence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="border-lp-border relative isolate h-[440px] w-full overflow-hidden rounded-[20px] border sm:h-[520px] lg:h-[600px] lg:rounded-[28px]">
      <div className="lp-mesh absolute inset-0 z-0" />
      <div className="lp-mesh-grain absolute inset-0 z-1" />

      {/* Frosted halo + window, centered and bleeding off the panel's bottom. */}
      <div className="absolute inset-0 z-2 flex justify-center pt-10 sm:pt-14 lg:pt-20">
        <div className="h-full w-[calc(100%-2.25rem)] rounded-t-[26px] bg-white/20 p-2 pb-0 backdrop-blur-md sm:w-[min(880px,calc(100%-4rem))] lg:w-[min(880px,calc(100%-6rem))]">
          <div className="border-lp-fg/10 flex h-full w-full flex-col overflow-hidden rounded-t-[20px] border border-b-0 bg-white shadow-[0_24px_60px_rgba(23,23,23,0.18)]">
            <div className="border-lp-border/70 flex items-center gap-2.5 border-b px-5 py-3.5 sm:px-7">
              <span
                className="size-2 rounded-full"
                style={{ background: "var(--lp-accent)" }}
              />
              <span className="text-lp-fg text-sm font-semibold">
                Front desk
              </span>
              <span className="text-lp-muted/70 ml-auto text-xs">Today</span>
            </div>

            <div className="divide-lp-border/70 flex-1 divide-y px-5 sm:px-7">
              {EVENTS.map((event, index) => {
                if (index < EVENTS.length - revealed) return null
                return (
                  <div
                    key={event.title}
                    className="lp-msg-in flex items-center gap-4 py-4 sm:py-5"
                  >
                    <span
                      className="grid size-9 shrink-0 place-items-center rounded-full text-white"
                      style={{ background: "var(--lp-accent)" }}
                    >
                      <event.icon className="size-4" />
                    </span>

                    <div className="min-w-0 flex-1">
                      <p className="text-lp-fg truncate text-sm font-medium">
                        {event.title}
                      </p>
                      <p className="text-lp-muted mt-1 truncate text-sm">
                        {event.detail}
                      </p>
                    </div>

                    {event.chip && (
                      <span
                        className={cn(
                          "border-lp-border text-lp-fg hidden shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium whitespace-nowrap sm:inline-flex",
                          event.live && "lp-card-selected",
                        )}
                      >
                        {event.live && (
                          <span
                            className="size-1.5 rounded-full motion-safe:animate-pulse"
                            style={{ background: "var(--lp-accent)" }}
                          />
                        )}
                        {event.chip}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
